#!/usr/bin/env node

import { LLMail } from '../LLMail';
import yargs from 'yargs';
import { hideBin } from 'yargs/helpers';
import { loadLlmailConfig, saveLlmailConfig, LlmailConfig } from '../utils/config.js';
import { realFileSystemAdapter } from '../utils/file-system-adapter.js';
import path from 'path';
import inquirer from 'inquirer';
import fs from 'fs-extra';
import chalk from 'chalk';
import { logger } from '../utils/logger.js';
import { createRequire } from 'module';

// Configuration presets
const CONFIG_PRESETS = {
  minimal: {
    description: 'active/inactive issues',
    types_list: [],
    active_states: [],
    inactive_reasons: [],
    issues_dir: 'issues',
    inbox_dir: 'inbox'
  },
  simple: {
    description: '',
    types_list: ['issue', 'doc', 'test', 'bug'],
    active_states: ['active'],
    inactive_reasons: ['done', 'fixed'],
    issues_dir: 'issues',
    inbox_dir: 'inbox'
  },
  full: {
    description: '',
    types_list: ['issue', 'doc', 'feat', 'test', 'bug', 'spec', 'plan'],
    active_states: ['active', 'blocked', 'planning'],
    inactive_reasons: ['done', 'later', 'fixed', 'old'],
    issues_dir: 'issues',
    inbox_dir: 'inbox'
  }
};

function formatPresetDetails(preset: typeof CONFIG_PRESETS[keyof typeof CONFIG_PRESETS]): string {
  const lines = [];
  
  // Add description if present
  if (preset.description) {
    lines.push(`(${preset.description})`);
  }

  // Add types if present
  if (preset.types_list.length) {
    lines.push(`  ${chalk.gray('types:')} ${chalk.blue(preset.types_list.join(', '))}`);
  }

  // Add active states if present
  if (preset.active_states.length) {
    lines.push(`  ${chalk.gray('active states:')} ${chalk.green(preset.active_states.join(', '))}`);
  }

  // Add inactive reasons if present
  if (preset.inactive_reasons.length) {
    lines.push(`  ${chalk.gray('inactive states:')} ${chalk.yellow(preset.inactive_reasons.join(', '))}`);
  }

  return lines.length ? `\n${lines.join('\n')}` : '';
}

function validateAndCleanInput(input: string): string[] {
  return input
    .split(',')
    .map(item => item.trim().toLowerCase())
    .filter(Boolean)
    .filter(item => /^[a-z0-9]+$/.test(item));
}

function displayConfig(config: LlmailConfig, hasLLMailTest: boolean = false): void {
  logger.info('\nConfiguration Summary:');
  logger.info('---------------------');
  
  // Check if we're using minimal config by checking if all lists are empty
  const isMinimal = !config.types_list.length && !config.active_states.length && !config.inactive_reasons.length;
  
  // Only show fields if they have values and we're not using minimal config
  if (!isMinimal) {
    if (config.types_list.length) {
      logger.info('Types:', config.types_list.join(', '));
    }
    if (config.active_states.length) {
      logger.info('Active States:', config.active_states.join(', '));
    }
    if (config.inactive_reasons.length) {
      logger.info('Inactive Reasons:', config.inactive_reasons.join(', '));
    }
  }
  
  // If all lists are empty, show a message about minimal configuration
  if (isMinimal) {
    logger.info('Using minimal configuration with default settings');
  }

  if (hasLLMailTest) {
    logger.success('✓ llmail-test plugin configured');
  }
}

async function checkLLMailTest(): Promise<boolean> {
  try {
    // First check if it's in the local package.json
    const packageJsonPath = path.join(process.cwd(), 'package.json');
    let inPackageJson = false;
    
    try {
      const packageJson = await fs.readJSON(packageJsonPath);
      inPackageJson = !!(packageJson.dependencies?.['llmail-test'] || packageJson.devDependencies?.['llmail-test']);
    } catch (error) {
      // Ignore error if package.json doesn't exist
    }

    // Then check if it's available in the global installation
    try {
      const require = createRequire(import.meta.url);
      require.resolve('llmail-test');
      return true;
    } catch (error) {
      // If not found in global installation, return the package.json result
      return inPackageJson;
    }
  } catch (error) {
    return false;
  }
}

async function getCustomConfig(): Promise<LlmailConfig> {
  const answers = await inquirer.prompt([
    {
      type: 'input',
      name: 'types',
      message: 'Enter types (comma-separated):',
      default: 'issue,doc',
      filter: validateAndCleanInput
    },
    {
      type: 'input',
      name: 'active_states',
      message: 'Enter active states (comma-separated):',
      default: 'active',
      filter: validateAndCleanInput
    },
    {
      type: 'input',
      name: 'inactive_reasons',
      message: 'Enter inactive reasons (comma-separated):',
      default: 'done,fixed',
      filter: validateAndCleanInput
    }
  ]);

  return {
    types_list: answers.types,
    active_states: answers.active_states,
    inactive_reasons: answers.inactive_reasons,
    issues_dir: 'issues',
    inbox_dir: 'inbox'
  };
}

async function main() {
  const argv = yargs(hideBin(process.argv))
    .usage('Usage: $0 init [options]')
    .option('config', {
      alias: 'c',
      type: 'string',
      description: 'Path to configuration file'
    })
    .help()
    .parseSync();

  const fsAdapter = realFileSystemAdapter;
  const rootDir = process.cwd();
  const configPath = argv.config || path.join(rootDir, 'llmail.yaml');

  try {
    // Check if config exists
    const configExists = await fsAdapter.exists(configPath);
    if (configExists) {
      const { proceed } = await inquirer.prompt([{
        type: 'confirm',
        name: 'proceed',
        message: 'llmail.yaml already exists. Do you want to overwrite it?',
        default: false
      }]);

      if (!proceed) {
        logger.info('Init cancelled.');
        process.exit(0);
      }
    }

    // Check for llmail-test
    const hasLLMailTest = await checkLLMailTest();

    // Get configuration type
    const { configType } = await inquirer.prompt([{
      type: 'list',
      name: 'configType',
      message: 'Choose config: minimal, simple, full, or custom',
      choices: [
        { 
          name: `Minimal${formatPresetDetails(CONFIG_PRESETS.minimal)}`,
          value: 'minimal' 
        },
        { 
          name: `Simple${formatPresetDetails(CONFIG_PRESETS.simple)}`,
          value: 'simple' 
        },
        { 
          name: `Full${formatPresetDetails(CONFIG_PRESETS.full)}`,
          value: 'full' 
        },
        { 
          name: 'Custom (configure your own)',
          value: 'custom' 
        }
      ]
    }]);

    // Get base config
    let config: LlmailConfig;
    if (configType === 'custom') {
      config = await getCustomConfig();
    } else {
      config = { ...CONFIG_PRESETS[configType as keyof typeof CONFIG_PRESETS] };
    }

    // Add llmail-test configuration if present
    if (hasLLMailTest) {
      // For minimal config, just add the plugin name without modifying types/states
      if (configType === 'minimal') {
        config.plugins = ['llmail-test'];
      } else {
        // For other configs, add the types and states
        config.types_list = [...new Set([...config.types_list, 'test'])];
        config.active_states = [...new Set([...config.active_states, 'pass', 'fail'])];
        if (!config.plugins) {
          config.plugins = ['llmail-test'];
        } else if (Array.isArray(config.plugins)) {
          if (typeof config.plugins[0] === 'string') {
            (config.plugins as string[]).push('llmail-test');
          } else {
            (config.plugins as Array<{name: string; config?: Record<string, any>}>).push({ name: 'llmail-test' });
          }
        }
      }
    }

    // Display final configuration
    displayConfig(config, hasLLMailTest);

    // Confirm save
    const { confirmSave } = await inquirer.prompt([{
      type: 'confirm',
      name: 'confirmSave',
      message: 'Save this configuration?',
      default: true
    }]);

    if (!confirmSave) {
      logger.info('Init cancelled.');
      process.exit(0);
    }

    // Save config
    await saveLlmailConfig(fsAdapter, config, configPath);

    // Initialize LLMail
    const llmail = new LLMail({ 
      fs: fsAdapter, 
      config,
      // Only skip plugins if it's minimal config AND we don't have llmail-test
      plugins: (configType === 'minimal' && !hasLLMailTest) ? [] : undefined 
    });
    await llmail.init();

    logger.success('\n✓ LLMail initialized successfully.');
    logger.info(chalk.gray('Configuration saved to:'), chalk.blue(configPath));
  } catch (error) {
    logger.error('Error initializing LLMail:', error);
    process.exit(1);
  }
}

export async function initCommand(argv: any): Promise<void> {
  const fsAdapter = realFileSystemAdapter;
  const rootDir = process.cwd();
  const configPath = argv.config || path.join(rootDir, 'llmail.yaml');

  try {
    // Check if config exists
    const configExists = await fsAdapter.exists(configPath);
    if (configExists) {
      const { proceed } = await inquirer.prompt([{
        type: 'confirm',
        name: 'proceed',
        message: 'llmail.yaml already exists. Do you want to overwrite it?',
        default: false
      }]);

      if (!proceed) {
        logger.info('Init cancelled.');
        process.exit(0);
      }
    }

    // Check for llmail-test
    const hasLLMailTest = await checkLLMailTest();

    // Get configuration type
    const { configType } = await inquirer.prompt([{
      type: 'list',
      name: 'configType',
      message: 'Choose config: minimal, simple, full, or custom',
      choices: [
        { 
          name: `Minimal${formatPresetDetails(CONFIG_PRESETS.minimal)}`,
          value: 'minimal' 
        },
        { 
          name: `Simple${formatPresetDetails(CONFIG_PRESETS.simple)}`,
          value: 'simple' 
        },
        { 
          name: `Full${formatPresetDetails(CONFIG_PRESETS.full)}`,
          value: 'full' 
        },
        { 
          name: 'Custom (configure your own)',
          value: 'custom' 
        }
      ]
    }]);

    // Get base config
    let config: LlmailConfig;
    if (configType === 'custom') {
      config = await getCustomConfig();
    } else {
      config = { ...CONFIG_PRESETS[configType as keyof typeof CONFIG_PRESETS] };
    }

    // Add llmail-test configuration if present
    if (hasLLMailTest) {
      // For minimal config, just add the plugin name without modifying types/states
      if (configType === 'minimal') {
        config.plugins = ['llmail-test'];
      } else {
        // For other configs, add the types and states
        config.types_list = [...new Set([...config.types_list, 'test'])];
        config.active_states = [...new Set([...config.active_states, 'pass', 'fail'])];
        if (!config.plugins) {
          config.plugins = ['llmail-test'];
        } else if (Array.isArray(config.plugins)) {
          if (typeof config.plugins[0] === 'string') {
            (config.plugins as string[]).push('llmail-test');
          } else {
            (config.plugins as Array<{name: string; config?: Record<string, any>}>).push({ name: 'llmail-test' });
          }
        }
      }
    }

    // Display final configuration
    displayConfig(config, hasLLMailTest);

    // Confirm save
    const { confirmSave } = await inquirer.prompt([{
      type: 'confirm',
      name: 'confirmSave',
      message: 'Save this configuration?',
      default: true
    }]);

    if (!confirmSave) {
      logger.info('Init cancelled.');
      process.exit(0);
    }

    // Save config
    await saveLlmailConfig(fsAdapter, config, configPath);

    // Initialize LLMail
    const llmail = new LLMail({ 
      fs: fsAdapter, 
      config,
      // Only skip plugins if it's minimal config AND we don't have llmail-test
      plugins: (configType === 'minimal' && !hasLLMailTest) ? [] : undefined 
    });
    await llmail.init();

    logger.success('\n✓ LLMail initialized successfully.');
    logger.info(chalk.gray('Configuration saved to:'), chalk.blue(configPath));
  } catch (error) {
    logger.error('Error initializing LLMail:', error);
    throw error;
  }
}

// Only run main() if this file is being run directly
if (import.meta.url === `file://${process.argv[1]}`) {
  main();
} 